/**
 * LeadMagnetPopup — the one marketing interstitial. It used to offer a "free
 * PDF guide" that was never sent; now it promotes the actual product: TodAidesign
 * system design — design it yourself in the browser, or order a done-for-you
 * design.
 *
 * Three skins, one component, picked at show time from what is on the page:
 *   • shop      — the light Todae storefront (.todae-shop chrome present): a
 *                 white card styled like the store home's design band — pill
 *                 label, Plus Jakarta Sans, blue pill buttons, step tiles.
 *   • marketing — the planner's light home page (.todai-marketing present):
 *                 the same card in the planner's own vocabulary (.tdm-*), which
 *                 mirrors the store's system, so the two are visually one.
 *   • planner   — the dark .todai-cinematic pages: eyebrow tick, display
 *                 headline, mono fact ledger, primary + ghost CTA row.
 *
 * Email capture stays as a secondary, explicit opt-in ("design tips"), so the
 * leads funnel keeps working — but the headline ask is the designer, not a form.
 *
 * Shows once per session, 45 s in, to anonymous visitors on marketing pages.
 * Inside the shop build the CTAs cross to the planner origin (plannerUrl).
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { supabase } from "@/integrations/supabase/client";
import { isShop, plannerUrl } from "@/config/platform";
import { formatUSD } from "@/config/marketplace";
import { toast } from "sonner";

const SESSION_KEY = "lead_magnet_shown";
const EXIT_INTENT_KEY = "exit_intent_shown";
const SHOW_DELAY_MS = 45_000;

const MARKETING_PATH_BLACKLIST = [
  "/dashboard",
  "/cctv-design",
  "/auth",
  "/settings",
];

// Mirrors DESIGN_FEE on /request-design — the done-for-you price we quote.
const DESIGN_FEE_USD = 20;

const DESIGNER_PATH = "/cctv-design";
const ORDER_DESIGN_PATH = "/request-design";

type Skin = "shop" | "marketing" | "planner";

/** The class vocabulary of the two light skins. Same structure, same token
 *  names (--line, --ink, --brand…) — only the prefix differs. */
const LIGHT_CLASSES: Record<"shop" | "marketing", {
  root: string; body: string; label: string; h2: string; p: string; eyebrow: string;
  steps: string; step: string; btn: string; primary: string; ghost: string; lg: string;
}> = {
  shop: {
    root: "todae-shop", body: "tds-design__body", label: "tds-design__label", h2: "tds-h2", p: "tds-p", eyebrow: "tds-eyebrow",
    steps: "tds-design__steps", step: "tds-design__step", btn: "tds-btn", primary: "tds-btn--primary", ghost: "tds-btn--ghost", lg: "tds-btn--lg",
  },
  marketing: {
    root: "todai-marketing", body: "tdm-band__body", label: "tdm-band__label", h2: "tdm-h2", p: "tdm-p", eyebrow: "tdm-eyebrow",
    steps: "tdm-band__steps", step: "tdm-band__step", btn: "tdm-btn", primary: "tdm-btn--primary", ghost: "tdm-btn--ghost", lg: "tdm-btn--lg",
  },
};

/** Planner route: a router <Link> inside the planner, a full cross-origin
 *  anchor from the shop build. Same visual class either way. */
function PlannerLink({
  path,
  className,
  onClick,
  children,
}: {
  path: string;
  className: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const lp = useLocalizedPath();
  if (isShop) {
    return (
      <a href={plannerUrl(path)} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={lp(path)} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export const LeadMagnetPopup = () => {
  const [visible, setVisible] = useState(false);
  const [skin, setSkin] = useState<Skin>("planner");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !consent || loading) return;

      setLoading(true);

      try {
        const { error } = await supabase.from("leads").upsert(
          {
            email: email.trim().toLowerCase(),
            source: "design_promo",
            page_url: window.location.pathname,
          },
          { onConflict: "email" }
        );

        if (error) throw error;

        // Keep localStorage flag so popup won't show again
        localStorage.setItem("lead_email_captured", "1");
        setSubmitted(true);
      } catch (err) {
        console.error("Failed to save lead:", err);
        toast.error(t("toast_generic_error", { defaultValue: "Something went wrong. Please try again." }));
      } finally {
        setLoading(false);
      }
    },
    [email, consent, loading, t]
  );

  useEffect(() => {
    // Don't show for authenticated users
    if (user) return;

    // Don't show if already shown this session
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Don't show if email was already captured
    if (localStorage.getItem("lead_email_captured")) return;

    // Don't show on non-marketing pages
    const path = window.location.pathname;
    if (MARKETING_PATH_BLACKLIST.some((p) => path.startsWith(p))) return;

    const timer = setTimeout(() => {
      // Check again at trigger time — exit intent may have fired in the meantime
      if (sessionStorage.getItem(EXIT_INTENT_KEY)) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;

      sessionStorage.setItem(SESSION_KEY, "1");
      // The chrome on the page decides the skin: the shop build and the /todae
      // pages inside the planner build both mount .todae-shop; the planner's
      // light home page mounts .todai-marketing; everything else is dark.
      setSkin(
        isShop || document.querySelector(".todae-shop")
          ? "shop"
          : document.querySelector(".todai-marketing")
            ? "marketing"
            : "planner",
      );
      setVisible(true);
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user]);

  if (!visible) return null;

  const price = formatUSD(DESIGN_FEE_USD);
  const copy = {
    eyebrow: t("dp_eyebrow", { defaultValue: "TodAidesign · System design" }),
    title: t("dp_title", { defaultValue: "Design your CCTV system. Or have us do it." }),
    desc: t("dp_desc", {
      defaultValue:
        "Plan camera and alarm coverage on satellite maps and real floor plans, with true lens physics and DORI per IEC 62676-4. Free to design a full project — or send us the site and our team designs it for you.",
    }),
    facts: [
      { dt: t("dp_fact_canvas_dt", { defaultValue: "Canvas" }), dd: t("dp_fact_canvas_dd", { defaultValue: "Maps + floor plans" }) },
      { dt: t("dp_fact_physics_dt", { defaultValue: "Physics" }), dd: t("dp_fact_physics_dd", { defaultValue: "DORI · IEC 62676-4" }) },
      { dt: t("dp_fact_dfy_dt", { defaultValue: "Done for you" }), dd: t("dp_fact_dfy_dd", { defaultValue: "From {{price}}", price }) },
    ],
    ctaDesign: t("dp_cta_design", { defaultValue: "Start designing — free" }),
    ctaOrder: t("dp_cta_order", { defaultValue: "Have us design it — {{price}}", price }),
    tipsLabel: t("dp_tips_label", { defaultValue: "Not ready yet? Get CCTV design tips by email." }),
    emailPlaceholder: t("dp_email_placeholder", { defaultValue: "Your email" }),
    consent: t("dp_consent", { defaultValue: "I agree to receive occasional CCTV design tips by email. Unsubscribe anytime." }),
    tipsCta: t("dp_tips_cta", { defaultValue: "Send me tips" }),
    noThanks: t("dp_no_thanks", { defaultValue: "No thanks" }),
    successEyebrow: t("dp_success_eyebrow", { defaultValue: "Subscribed" }),
    successTitle: t("dp_success_title", { defaultValue: "You're on the list." }),
    successDesc: t("dp_success_desc", {
      defaultValue: "Design tips will land in your inbox. Meanwhile, the designer is free to use — a full project, no card.",
    }),
    openDesigner: t("dp_open_designer", { defaultValue: "Open the designer" }),
    close: t("common:close_popup", { defaultValue: "Close popup" }),
  };

  /* ─────────────────────── LIGHT skins (shop, marketing) ───────────── */
  if (skin !== "planner") {
    const c = LIGHT_CLASSES[skin];
    return (
      /* The scope class re-points the tokens (fonts, --brand, --line, --ink) for
         this subtree; the scrim is inline so the scope's page background doesn't
         paint the overlay. */
      <div
        className={`${c.root} fixed inset-0 z-[90] flex items-center justify-center p-4`}
        style={{ background: "rgba(19, 21, 25, 0.45)" }}
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dp-title"
      >
        <div
          className="relative w-full max-w-xl"
          style={{
            padding: "clamp(1.5rem, 3vw, 2.25rem)",
            borderRadius: 18,
            background: "linear-gradient(135deg, #FFFFFF 0%, #EAF1FD 100%)",
            border: "1px solid var(--line)",
            boxShadow: "0 30px 80px -30px rgba(16,24,40,.45)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3"
            style={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 999, border: 0, background: "transparent", color: "var(--ink-2)", cursor: "pointer" }}
            aria-label={copy.close}
          >
            <X size={18} />
          </button>

          {submitted ? (
            <div className={c.body}>
              <span className={c.label}>
                <CheckCircle2 size={13} aria-hidden="true" style={{ color: "var(--brand)" }} />
                {copy.successEyebrow}
              </span>
              <h2 id="dp-title" className={c.h2} style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)" }}>
                {copy.successTitle}
              </h2>
              <p className={c.p}>{copy.successDesc}</p>
              <div>
                <PlannerLink path={DESIGNER_PATH} className={`${c.btn} ${c.primary} ${c.lg}`} onClick={handleClose}>
                  {copy.openDesigner} <ArrowRight size={17} aria-hidden="true" />
                </PlannerLink>
              </div>
            </div>
          ) : (
            <div className={c.body}>
              <span className={c.label}><i aria-hidden="true" /> {copy.eyebrow}</span>
              <h2 id="dp-title" className={c.h2} style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)" }}>
                {copy.title}
              </h2>
              <p className={c.p}>{copy.desc}</p>

              <ul className={c.steps}>
                {copy.facts.map((f) => (
                  <li key={f.dt} className={c.step}>
                    <b>{f.dt}</b>
                    <span>{f.dd}</span>
                  </li>
                ))}
              </ul>

              <div style={{ display: "flex", flexWrap: "wrap", gap: ".6rem" }}>
                <PlannerLink path={DESIGNER_PATH} className={`${c.btn} ${c.primary} ${c.lg}`} onClick={handleClose}>
                  {copy.ctaDesign} <ArrowRight size={17} aria-hidden="true" />
                </PlannerLink>
                <PlannerLink path={ORDER_DESIGN_PATH} className={`${c.btn} ${c.ghost} ${c.lg}`} onClick={handleClose}>
                  {copy.ctaOrder}
                </PlannerLink>
              </div>

              {/* Secondary: explicit, unchecked opt-in (POPIA / GDPR). */}
              <form onSubmit={handleSubmit} style={{ marginTop: ".5rem", paddingTop: "1rem", borderTop: "1px solid var(--line)" }}>
                <p className={c.eyebrow} style={{ color: "var(--ink-2)" }}>{copy.tipsLabel}</p>
                <div style={{ display: "flex", gap: ".5rem" }}>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={copy.emailPlaceholder}
                    aria-label={copy.emailPlaceholder}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 42,
                      padding: "0 1rem",
                      borderRadius: 999,
                      border: "1px solid var(--line-strong, var(--line))",
                      background: "#fff",
                      color: "var(--ink)",
                      font: "400 .9rem/1 var(--font-body)",
                      outline: 0,
                    }}
                  />
                  <button type="submit" disabled={loading || !consent} className={`${c.btn} ${c.ghost}`} style={{ flex: "none" }}>
                    {loading ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : copy.tipsCta}
                  </button>
                </div>
                <label className={c.p} style={{ display: "flex", alignItems: "flex-start", gap: ".55rem", fontSize: 12, marginTop: ".6rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    style={{ marginTop: 2, flex: "none", accentColor: "var(--brand)" }}
                  />
                  <span>{copy.consent}</span>
                </label>
              </form>

              <button
                type="button"
                onClick={handleClose}
                className={c.p}
                style={{ background: "none", border: 0, cursor: "pointer", fontSize: 12.5, color: "var(--ink-3)", padding: 0, alignSelf: "center" }}
              >
                {copy.noThanks}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ─────────────────────────── PLANNER skin ────────────────────────── */
  return (
    /* .todai-cinematic re-points every token for this subtree; the scrim is
       inline so the scope's own background doesn't paint the overlay. */
    <div
      className="todai-cinematic fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(7, 6, 9, 0.72)" }}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dp-title"
    >
      <div
        className="cin-panel relative w-full max-w-xl overflow-hidden"
        style={{ gap: 0, padding: "clamp(1.5rem, 3vw, 2.25rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cin-grid-bg" aria-hidden="true" />

        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={copy.close}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative z-[1]">
          {submitted ? (
            <div className="flex flex-col items-start">
              <p className="cin-eyebrow" style={{ marginBottom: 16 }}>
                <CheckCircle2 size={14} className="cin-accent-text" aria-hidden="true" />
                {copy.successEyebrow}
              </p>
              <h2 id="dp-title" className="cin-h2" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)" }}>
                {copy.successTitle}
              </h2>
              <p className="cin-lead" style={{ fontSize: "0.9375rem", marginTop: 12 }}>{copy.successDesc}</p>
              <div className="cin-hero__cta-row" style={{ marginTop: 24 }}>
                <PlannerLink path={DESIGNER_PATH} className="cin-btn cin-btn--primary" onClick={handleClose}>
                  {copy.openDesigner} <ArrowRight size={16} aria-hidden="true" />
                </PlannerLink>
              </div>
            </div>
          ) : (
            <>
              <p className="cin-eyebrow" style={{ marginBottom: 16 }}>{copy.eyebrow}</p>

              <h2 id="dp-title" className="cin-h2" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)" }}>
                {copy.title}
              </h2>

              <p className="cin-lead" style={{ fontSize: "0.9375rem", marginTop: 12 }}>{copy.desc}</p>

              <dl className="cin-cta__facts" style={{ marginTop: 20 }}>
                {copy.facts.map((f) => (
                  <div key={f.dt} className="cin-fact">
                    <dt>{f.dt}</dt>
                    <dd>{f.dd}</dd>
                  </div>
                ))}
              </dl>

              <div className="cin-hero__cta-row" style={{ marginTop: 20 }}>
                <PlannerLink path={DESIGNER_PATH} className="cin-btn cin-btn--primary" onClick={handleClose}>
                  {copy.ctaDesign} <ArrowRight size={16} aria-hidden="true" />
                </PlannerLink>
                <PlannerLink path={ORDER_DESIGN_PATH} className="cin-btn cin-btn--ghost" onClick={handleClose}>
                  {copy.ctaOrder}
                </PlannerLink>
              </div>

              {/* Secondary: explicit, unchecked opt-in (GDPR / POPIA). */}
              <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
                <p className="cin-field__label">{copy.tipsLabel}</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={copy.emailPlaceholder}
                    aria-label={copy.emailPlaceholder}
                    className="cin-input"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button
                    type="submit"
                    disabled={loading || !consent}
                    className="cin-btn cin-btn--ghost"
                    style={{ height: "2.5rem", padding: "0 1rem", flex: "none", opacity: consent ? 1 : 0.55 }}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : copy.tipsCta}
                  </button>
                </div>
                <label className="cin-note flex items-start gap-2 cursor-pointer" style={{ marginTop: 10, letterSpacing: 0, textTransform: "none" }}>
                  <input
                    type="checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 flex-shrink-0 accent-[hsl(var(--primary))]"
                  />
                  <span>{copy.consent}</span>
                </label>
              </form>

              <button
                type="button"
                onClick={handleClose}
                className="cin-note block w-full text-center hover:text-foreground transition-colors"
                style={{ marginTop: 16 }}
              >
                {copy.noThanks}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
